import React, { useState } from "react";
import { Mail, Send } from "lucide-react";
import { bandData } from "../mock";
import { useToast } from "../hooks/use-toast";
import { Toaster } from "./ui/sonner";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Booking = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    venue: "",
    eventDate: "",
    eventType: "",
    message: ""
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Save to database
      await axios.post(`${API}/bookings`, formData);
      
      // Format email body
      const emailSubject = `Booking Request - ${formData.venue} on ${formData.eventDate}`;
      const emailBody = `
New Booking Request for Searching Fusion

Event Details:
- Date: ${formData.eventDate}
- Venue: ${formData.venue}
- Event Type: ${formData.eventType}

Contact Information:
- Name: ${formData.name}
- Email: ${formData.email}
- Phone: ${formData.phone || 'Not provided'}

Message:
${formData.message}
      `.trim();
      
      // Create mailto link
      const mailtoLink = `mailto:${bandData.email}?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;
      
      // Open email client
      window.location.href = mailtoLink;
      
      // Show success message
      toast.success("Opening your email client!", {
        description: "Please click Send to complete your booking request."
      });

      // Reset form
      setFormData({
        name: "",
        email: "",
        phone: "",
        venue: "",
        eventDate: "",
        eventType: "",
        message: ""
      });
    } catch (error) {
      console.error("Error submitting booking:", error);
      toast.error("Failed to submit booking", {
        description: "Please try again or email us directly at " + bandData.email
      });
    }
  };

  return (
    <section id="booking" className="py-20 bg-[#0a0a0f]">
      <Toaster position="top-center" richColors />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">
            Book Searching Fusion
          </h2>
          <div className="w-24 h-1 bg-[#ff6b35] mx-auto mb-6" />
          <p className="text-xl text-white/80 max-w-3xl mx-auto">
            Looking to bring high-energy fusion to your venue or event in 2026? Let's talk!
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div>
            <h3 className="text-2xl font-bold text-white mb-6">
              Get In Touch
            </h3>
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#ff6b35] rounded-full flex items-center justify-center flex-shrink-0">
                  <Mail size={24} className="text-white" />
                </div>
                <div>
                  <p className="text-white/60 mb-1">Email</p>
                  <a
                    href={`mailto:${bandData.email}`}
                    className="text-white text-lg hover:text-[#00d9ff]"
                  >
                    {bandData.email}
                  </a>
                </div>
              </div>

              <div className="bg-[#1a1a2e] rounded-lg p-6">
                <h4 className="text-xl font-bold text-white mb-4">
                  What We Offer:
                </h4>
                <ul className="space-y-3 text-white/80">
                  <li className="flex items-start gap-2">
                    <span className="text-[#00d9ff] mt-1">•</span>
                    <span>High-energy fusion performances blending prog, jazz, and Latin</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#00d9ff] mt-1">•</span>
                    <span>Professional power trio with years of combined experience</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#00d9ff] mt-1">•</span>
                    <span>Original compositions and fusion classics</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#00d9ff] mt-1">•</span>
                    <span>Flexible setlists for various venues and events</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Booking Form */}
          <div>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-white mb-2 font-medium">
                    Your Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#00d9ff]"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-white mb-2 font-medium">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#00d9ff]"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="phone" className="block text-white mb-2 font-medium">
                    Phone
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#00d9ff]"
                  />
                </div>
                <div>
                  <label htmlFor="venue" className="block text-white mb-2 font-medium">
                    Venue Name *
                  </label>
                  <input
                    type="text"
                    id="venue"
                    name="venue"
                    value={formData.venue}
                    onChange={handleChange}
                    required
                    className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#00d9ff]"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="eventDate" className="block text-white mb-2 font-medium">
                    Preferred Date *
                  </label>
                  <input
                    type="date"
                    id="eventDate"
                    name="eventDate"
                    value={formData.eventDate}
                    onChange={handleChange}
                    required
                    min="2026-01-01"
                    className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#00d9ff]"
                  />
                </div>
                <div>
                  <label htmlFor="eventType" className="block text-white mb-2 font-medium">
                    Event Type *
                  </label>
                  <select
                    id="eventType"
                    name="eventType"
                    value={formData.eventType}
                    onChange={handleChange}
                    required
                    className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#00d9ff]"
                  >
                    <option value="">Select event type</option>
                    <option value="concert">Concert/Live Show</option>
                    <option value="festival">Festival</option>
                    <option value="private">Private Event</option>
                    <option value="corporate">Corporate Event</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="message" className="block text-white mb-2 font-medium">
                  Additional Details *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={4}
                  className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#00d9ff] resize-none"
                  placeholder="Tell us more about your event, expected audience size, budget range, etc."
                />
              </div>

              <button
                type="submit"
                className="w-full bg-[#ff6b35] hover:bg-[#d1503a] text-white px-8 py-4 rounded-full font-bold text-lg flex items-center justify-center gap-2 shadow-lg hover:shadow-xl"
              >
                <Send size={20} />
                Submit Booking Request
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Booking;
